import { Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AppShell from './components/layout/AppShell';
import RequireAuth from './features/auth/RequireAuth';
import { useAuth } from './features/auth/useAuth';
import Spinner from './components/ui/Spinner';
import ErrorState from './components/ui/ErrorState';
import type { Role } from './lib/types';
import { lazyWithReload, reloadOnceForChunkError } from './lib/lazy-with-reload';

const LoginPage = lazyWithReload(() => import('./features/auth/LoginPage'));
const ForgotPasswordPage = lazyWithReload(() => import('./features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithReload(() => import('./features/auth/ResetPasswordPage'));
const SigningPage = lazyWithReload(() => import('./features/signing/SigningPage'));
const OnboardPage = lazyWithReload(() => import('./features/onboarding/OnboardPage'));
const TrainingCertificatePage = lazyWithReload(() => import('./features/training-certificate/TrainingCertificatePage'));
const CampaignsListPage = lazyWithReload(() => import('./features/campaigns/CampaignsListPage'));
const CampaignFormPage = lazyWithReload(() => import('./features/campaigns/CampaignFormPage'));
const CampaignDetailPage = lazyWithReload(() => import('./features/campaigns/CampaignDetailPage'));
const VendorsListPage = lazyWithReload(() => import('./features/vendors/VendorsListPage'));
const VendorFormPage = lazyWithReload(() => import('./features/vendors/VendorFormPage'));
const ContractsListPage = lazyWithReload(() => import('./features/contracts/ContractsListPage'));
const ContractCreatePage = lazyWithReload(() => import('./features/contracts/ContractCreatePage'));
const WetUploadPage = lazyWithReload(() => import('./features/contracts/WetUploadPage'));
const ContractDetailPage = lazyWithReload(() => import('./features/contracts/ContractDetailPage'));
const ResourcesPage = lazyWithReload(() => import('./features/resources/ResourcesPage'));
const OnboardersPage = lazyWithReload(() => import('./features/onboarders/OnboardersPage'));
const ExportsPage = lazyWithReload(() => import('./features/exports/ExportsPage'));
const ApprovalQueuePage = lazyWithReload(() => import('./features/approvals/ApprovalQueuePage'));
const PreparationQueuePage = lazyWithReload(() => import('./features/preparation/PreparationQueuePage'));
const ContractFieldEditorPage = lazyWithReload(() => import('./features/preparation/ContractFieldEditorPage'));
const UsersPage = lazyWithReload(() => import('./features/users/UsersPage'));
const ApiKeysPage = lazyWithReload(() => import('./features/api-keys/ApiKeysPage'));
const UnmatchedCertificatesPage = lazyWithReload(
  () => import('./features/unmatched-certificates/UnmatchedCertificatesPage'),
);
const EmailSettingsPage = lazyWithReload(() => import('./features/settings/EmailSettingsPage'));
const AssistantPage = lazyWithReload(() => import('./features/assistant/AssistantPage'));

/**
 * Vite's own documented hook for this exact failure mode: fires when a
 * dynamically-imported chunk 404s (stale tab across a deploy) even for a
 * preload/prefetch that never went through one of the lazyWithReload()
 * wrappers above (e.g. a route Vite prefetched speculatively). Same
 * guarded-reload path as lazyWithReload — one reload per 30s, via
 * sessionStorage.
 */
function useChunkPreloadErrorReload() {
  useEffect(() => {
    function handlePreloadError() {
      reloadOnceForChunkError();
    }
    window.addEventListener('vite:preloadError', handlePreloadError);
    return () => window.removeEventListener('vite:preloadError', handlePreloadError);
  }, []);
}

/**
 * Gates a route element on a set of allowed roles — mirrors SideNav's
 * role-based filtering (D55: a Submitter is API-denied everything but
 * vendors/campaigns(list)/contracts; S39/D95 adds Staff, campaign-scoped).
 * Used inside the already-RequireAuth'd route tree, so a user without one of
 * `roles` (e.g. a Submitter or Staff typing a hidden URL or using an old
 * bookmark) is simply bounced to the default route rather than to /login,
 * instead of hitting a confusing wall of 403s. Keeps the underlying page
 * dumb: it never has to know which roles are allowed in. This is cosmetic
 * defence-in-depth only — the API is the real authorization gate, and this
 * component must never be the ONLY thing standing between a role and an
 * action it shouldn't take.
 *
 * Fails closed: an unknown or missing role (no `user`, or a `roles` array
 * that doesn't overlap `roles` at all — e.g. a role literal the client
 * doesn't recognize yet) renders nothing but the redirect. There is no
 * default-permit branch.
 */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  const allowed = !!user?.roles.some((r) => roles.includes(r));

  if (!allowed) {
    return <Navigate to="/campaigns" replace />;
  }

  return <>{children}</>;
}

/** RequireAdmin — RequireRole scoped to Admin only. Kept exported for existing direct tests. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  return <RequireRole roles={['Admin']}>{children}</RequireRole>;
}

function PageLoader() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '16rem' }}
    >
      <Spinner size="lg" />
    </div>
  );
}

export default function AppRoutes() {
  useChunkPreloadErrorReload();
  return (
    <Routes>
      {/* ── Public routes ── */}
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageLoader />}>
            <LoginPage />
          </Suspense>
        }
      />
      {/* D26 self-service forgot/reset password — public, no AppShell chrome. */}
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageLoader />}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      {/* Vendor/EGC signing link (D26) — no auth, no AppShell chrome. */}
      <Route
        path="/sign/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <SigningPage />
          </Suspense>
        }
      />
      {/* Public rep onboarding link (Wave 4) — no auth, no AppShell chrome. */}
      <Route
        path="/onboard/:slug"
        element={
          <Suspense fallback={<PageLoader />}>
            <OnboardPage />
          </Suspense>
        }
      />
      {/* SEV2 fix: a multi-segment path (mis-copied link, or a literal-slash
          XSS probe) misses the single-segment routes above and must NOT fall
          through to the generic "*" catch-all below, which sits inside
          RequireAuth and bounces an unauthenticated visitor to /login. These
          splat variants keep the same public page + Suspense wrapper so the
          slug/token still resolves to the page's own invalid-link state. */}
      <Route
        path="/sign/*"
        element={
          <Suspense fallback={<PageLoader />}>
            <SigningPage />
          </Suspense>
        }
      />
      <Route
        path="/onboard/*"
        element={
          <Suspense fallback={<PageLoader />}>
            <OnboardPage />
          </Suspense>
        }
      />
      {/* S50/R1: public agent certificate upload — no auth, no AppShell. Not under /api (nginx SPA guard). */}
      <Route
        path="/certificate"
        element={
          <Suspense fallback={<PageLoader />}>
            <TrainingCertificatePage />
          </Suspense>
        }
      />
      <Route
        path="/certificate/*"
        element={
          <Suspense fallback={<PageLoader />}>
            <TrainingCertificatePage />
          </Suspense>
        }
      />

      {/* ── Protected routes — wrapped in RequireAuth + AppShell ── */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/campaigns" replace />} />

        <Route
          path="/campaigns"
          element={
            <Suspense fallback={<PageLoader />}>
              <CampaignsListPage />
            </Suspense>
          }
        />
        <Route
          path="/campaigns/new"
          element={
            <Suspense fallback={<PageLoader />}>
              <CampaignFormPage />
            </Suspense>
          }
        />
        <Route
          path="/campaigns/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <CampaignDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/campaigns/:id/edit"
          element={
            <Suspense fallback={<PageLoader />}>
              <CampaignFormPage />
            </Suspense>
          }
        />

        <Route
          path="/vendors"
          element={
            <Suspense fallback={<PageLoader />}>
              <VendorsListPage />
            </Suspense>
          }
        />
        <Route
          path="/vendors/new"
          element={
            <Suspense fallback={<PageLoader />}>
              <VendorFormPage />
            </Suspense>
          }
        />
        {/* Canonical /vendors/:id (SEV3 fix) — no dedicated view page exists, so land on edit like a real route instead of falling through to the * catch-all. */}
        <Route path="/vendors/:id" element={<Navigate to="edit" replace />} />
        <Route
          path="/vendors/:id/edit"
          element={
            <Suspense fallback={<PageLoader />}>
              <VendorFormPage />
            </Suspense>
          }
        />

        <Route
          path="/contracts"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContractsListPage />
            </Suspense>
          }
        />
        <Route
          path="/contracts/new"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContractCreatePage />
            </Suspense>
          }
        />
        <Route
          path="/contracts/wet-upload"
          element={
            <Suspense fallback={<PageLoader />}>
              <WetUploadPage />
            </Suspense>
          }
        />
        <Route
          path="/contracts/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContractDetailPage />
            </Suspense>
          }
        />

        <Route
          path="/resources"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <ResourcesPage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/onboarders"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <OnboardersPage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/exports"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <ExportsPage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/approval-queue"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <ApprovalQueuePage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/preparation-queue"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <PreparationQueuePage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="/preparation/:templateVersionId"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <ContractFieldEditorPage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/assistant"
          element={
            <RequireRole roles={['Admin', 'Staff']}>
              <Suspense fallback={<PageLoader />}>
                <AssistantPage />
              </Suspense>
            </RequireRole>
          }
        />

        <Route
          path="/users"
          element={
            <RequireAdmin>
              <Suspense fallback={<PageLoader />}>
                <UsersPage />
              </Suspense>
            </RequireAdmin>
          }
        />

        <Route
          path="/api-keys"
          element={
            <RequireAdmin>
              <Suspense fallback={<PageLoader />}>
                <ApiKeysPage />
              </Suspense>
            </RequireAdmin>
          }
        />

        {/* S52/D146: Admin-only inbox of certificate uploads that matched no single person. */}
        <Route
          path="/unmatched-certificates"
          element={
            <RequireAdmin>
              <Suspense fallback={<PageLoader />}>
                <UnmatchedCertificatesPage />
              </Suspense>
            </RequireAdmin>
          }
        />

        <Route
          path="/settings/email"
          element={
            <RequireAdmin>
              <Suspense fallback={<PageLoader />}>
                <EmailSettingsPage />
              </Suspense>
            </RequireAdmin>
          }
        />

        {/* Catch-all: unrouted authed paths render a visible not-found inside the shell instead of silently redirecting */}
        <Route path="*" element={<ErrorState message="Page not found." />} />
      </Route>
    </Routes>
  );
}
